import React, {useState} from "react";
import { useTranslation } from "react-i18next";
import styles from "./TopicNameAndInfo.module.sass";
import cn from "classnames";
import Card from "../../../../../components/Card";
import TextInput from "../../../../../components/TextInput";
import Checkbox from "../../../../../components/Checkbox";
import { AVAILABLE_LANGS } from "../../../../../utils/constants";
import {toast} from "sonner";

const TopicNameAndInfo = ({
                            className,
                            active,
                            setActive,
                            title,
                            setTitle,
                            description,
                            setDescription,
                            updateTranslation,
                            getTranslation
                          }) => {
  const { t } = useTranslation();
  const [activeTitleTab, setActiveTitleTab] = useState('en');
  const [activeDescriptionTab, setActiveDescriptionTab] = useState('en');
  const [loadingTranslationTitle, setLoadingTranslationTitle] = useState(false);
  const [loadingTranslationDescription, setLoadingTranslationDescription] = useState(false);

  const handleTranslateTitle = async () => {
    setLoadingTranslationTitle(true);

    try {
      const baseText = getTranslation('title', 'en');
      if (!baseText) {
        toast.error(t('toast.pleaseEnterEnglishTitle'));
        return;
      }

      const url = `${process.env.REACT_APP_API_URL}/translation/deepl`;
      const authorizationHeader = {
        'Authorization': `Bearer ${JSON.parse(localStorage?.getItem('user')).token}`
      };

      for (const lang of AVAILABLE_LANGS) {
        if (lang !== 'en') {
          try {
            const response = await fetch(url, {
              method: 'POST',
              body: JSON.stringify({
                title: baseText,
                lang: lang.toUpperCase()
              }),
              headers: {
                'Content-Type': 'application/json',
                ...authorizationHeader
              }
            });

            if (response.ok) {
              const data = await response.json();
              const translation = data?.translations?.[0]?.text;
              if (translation) {
                updateTranslation('title', lang, translation);
              }
            }
          } catch (error) {
            console.error(`Error translating title to ${lang}:`, error);
          }
        }
      }
      toast.success(t('toast.titlesTranslated'));
    } catch (error) {
      console.error('Error translating title:', error);
      toast.error(t('toast.errorTranslatingTitles'));
    } finally {
      setLoadingTranslationTitle(false);
    }
  };

  const handleTranslateDescription = async () => {
    setLoadingTranslationDescription(true);

    try {
      const baseText = getTranslation('description', 'en');
      if (!baseText) {
        toast.error(t('toast.pleaseEnterEnglishDescription'));
        return;
      }

      const url = `${process.env.REACT_APP_API_URL}/translation/deepl`;
      const authorizationHeader = {
        'Authorization': `Bearer ${JSON.parse(localStorage?.getItem('user')).token}`
      };

      for (const lang of AVAILABLE_LANGS) {
        if (lang !== 'en') {
          try {
            const response = await fetch(url, {
              method: 'POST',
              body: JSON.stringify({
                title: baseText,
                lang: lang.toUpperCase()
              }),
              headers: {
                'Content-Type': 'application/json',
                ...authorizationHeader
              }
            });

            if (response.ok) {
              const data = await response.json();
              const translation = data?.translations?.[0]?.text;
              if (translation) {
                updateTranslation('description', lang, translation);
              }
            }
          } catch (error) {
            console.error(`Error translating description to ${lang}:`, error);
          }
        }
      }
      toast.success(t('toast.descriptionsTranslated'));
    } catch (error) {
      console.error('Error translating description:', error);
      toast.error(t('toast.errorTranslatingDescriptions'));
    } finally {
      setLoadingTranslationDescription(false);
    }
  };

  return (
      <Card className={cn(styles.card, className)} >
        <div className={styles.list}>
          {/* Active Toggle */}
          <div className={styles.item}>
            <div className={styles.fieldset}>
              <Checkbox
                  className={styles.checkbox}
                  content="Active"
                  value={active}
                  onChange={(e) => setActive(e.target.checked)}
              />
            </div>
          </div>

          {/* Title Section */}
          <div className={styles.item}>
            <label style={{ fontSize: 14, display: "flex", alignItems: "center" }}>
              <span>Topic title</span>
              <button
                  onClick={handleTranslateTitle}
                  style={{ marginLeft: 20 }}
                  className={cn("button-stroke button-small", styles.button)}
                  disabled={loadingTranslationTitle}
              >
                {loadingTranslationTitle ? (
                    <svg width="16" height="16" viewBox="0 0 38 38" xmlns="http://www.w3.org/2000/svg">
                      <defs>
                        <linearGradient x1="8.042%" y1="0%" x2="65.682%" y2="23.865%" id="a">
                          <stop stopColor="#000" stopOpacity="0" offset="0%" />
                          <stop stopColor="#000" stopOpacity=".631" offset="63.146%" />
                          <stop stopColor="#000" offset="100%" />
                        </linearGradient>
                      </defs>
                      <g fill="none" fillRule="evenodd">
                        <g transform="translate(1 1)">
                          <path d="M36 18c0-9.94-8.06-18-18-18" stroke="url(#a)" strokeWidth="2">
                            <animateTransform
                                attributeName="transform"
                                type="rotate"
                                from="0 18 18"
                                to="360 18 18"
                                dur="0.9s"
                                repeatCount="indefinite"
                            />
                          </path>
                          <circle fill="#000" cx="36" cy="18" r="1">
                            <animateTransform
                                attributeName="transform"
                                type="rotate"
                                from="0 18 18"
                                to="360 18 18"
                                dur="0.9s"
                                repeatCount="indefinite"
                            />
                          </circle>
                        </g>
                      </g>
                    </svg>
                ) : (
                    <span>Auto-translate</span>
                )}
              </button>
            </label>

            {/* Language Tabs for Title */}
            <div style={{ display: "flex", alignItems: "center", marginTop: 10 }} className={cn(styles.nav, "tablet-hide")}>
              {AVAILABLE_LANGS.map((lang, index) => (
                  <button
                      key={index}
                      className={cn(styles.link, {
                        [styles.active]: lang === activeTitleTab,
                      })}
                      onClick={() => setActiveTitleTab(lang)}
                  >
                    {lang.toUpperCase()}
                  </button>
              ))}
            </div>

            {/* Title Input */}
            <div style={{ marginTop: 10 }}>
              <TextInput
                  type="text"
                  required
                  placeholder="Topic title here"
                  value={getTranslation('title', activeTitleTab)}
                  onChange={(e) => updateTranslation('title', activeTitleTab, e.target.value)}
              />
            </div>
          </div>

          {/* Description Section */}
          <div className={styles.item}>
            <label style={{ fontSize: 14, display: "flex", alignItems: "center" }}>
              <span>Short description</span>
              <button
                  onClick={handleTranslateDescription}
                  style={{ marginLeft: 20 }}
                  className={cn("button-stroke button-small", styles.button)}
                  disabled={loadingTranslationDescription}
              >
                {loadingTranslationDescription ? (
                    <svg width="16" height="16" viewBox="0 0 38 38" xmlns="http://www.w3.org/2000/svg">
                      <defs>
                        <linearGradient x1="8.042%" y1="0%" x2="65.682%" y2="23.865%" id="a">
                          <stop stopColor="#000" stopOpacity="0" offset="0%" />
                          <stop stopColor="#000" stopOpacity=".631" offset="63.146%" />
                          <stop stopColor="#000" offset="100%" />
                        </linearGradient>
                      </defs>
                      <g fill="none" fillRule="evenodd">
                        <g transform="translate(1 1)">
                          <path d="M36 18c0-9.94-8.06-18-18-18" stroke="url(#a)" strokeWidth="2">
                            <animateTransform
                                attributeName="transform"
                                type="rotate"
                                from="0 18 18"
                                to="360 18 18"
                                dur="0.9s"
                                repeatCount="indefinite"
                            />
                          </path>
                          <circle fill="#000" cx="36" cy="18" r="1">
                            <animateTransform
                                attributeName="transform"
                                type="rotate"
                                from="0 18 18"
                                to="360 18 18"
                                dur="0.9s"
                                repeatCount="indefinite"
                            />
                          </circle>
                        </g>
                      </g>
                    </svg>
                ) : (
                    <span>Auto-translate</span>
                )}
              </button>
            </label>

            {/* Language Tabs for Description */}
            <div style={{ display: "flex", alignItems: "center", marginTop: 10 }} className={cn(styles.nav, "tablet-hide")}>
              {AVAILABLE_LANGS.map((lang, index) => (
                  <button
                      key={index}
                      className={cn(styles.link, {
                        [styles.active]: lang === activeDescriptionTab,
                      })}
                      onClick={() => setActiveDescriptionTab(lang)}
                  >
                    {lang.toUpperCase()}
                  </button>
              ))}
            </div>

            {/* Description Textarea */}
            <div style={{ marginTop: 10 }}>
            <textarea
                rows="4"
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #e0e0e0',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontFamily: 'inherit',
                  resize: 'vertical'
                }}
                placeholder="Write description here..."
                value={getTranslation('description', activeDescriptionTab)}
                onChange={(e) => updateTranslation('description', activeDescriptionTab, e.target.value)}
            />
            </div>
          </div>
        </div>
      </Card>
  );
};

export default TopicNameAndInfo;